/**
 * React Query hooks for remote matching tutor/student dashboards.
 * Source: docs/spec-matching-interface.md Part F §29.2-29.3
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import * as matchingService from '@/services/remote/matching.service';
import type {
  TutorDashboardItem,
  StudentDashboardItem,
  DashboardResponse,
  TutorDashboardParams,
  StudentDashboardParams,
} from '@/types/remote/matching.types';
import { useAuthStore } from '@/stores/authStore';

/** React Query key for `GET /matching/stats`. */
export const matchingStatsKey = (companyId: string) => ['matching', 'stats', companyId] as const;

/** React Query keys for remote matching tutor/student dashboards. */
export const matchingDashboardKeys = {
  all: ['matching', 'dashboard'] as const,
  tutor: (companyId: string, params?: TutorDashboardParams) =>
    [...matchingDashboardKeys.all, 'tutor', companyId, { params }] as const,
  student: (companyId: string, params?: StudentDashboardParams) =>
    [...matchingDashboardKeys.all, 'student', companyId, { params }] as const,
};

/** Tutor view of the main table (paginated, matching status). Spec §29.2. */
export const useTutorDashboard = (
  params?: TutorDashboardParams,
): UseQueryResult<DashboardResponse<TutorDashboardItem>> => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: matchingDashboardKeys.tutor(companyId!, params),
    queryFn: () => matchingService.getTutorDashboard(companyId!, params),
    // `params === undefined` is the caller's "inactive perspective" signal
    // (ConfigureMatchingView). Tutor/student views are mutually exclusive — omitting
    // `!!params` fires GET /matching/tutor/dashboard with no page/pageSize.
    enabled: !!companyId && !!params,
  });
};

/** Student view of the main table. Spec §29.3. */
export const useStudentDashboard = (
  params?: StudentDashboardParams,
): UseQueryResult<DashboardResponse<StudentDashboardItem>> => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  return useQuery({
    queryKey: matchingDashboardKeys.student(companyId!, params),
    queryFn: () => matchingService.getStudentDashboard(companyId!, params),
    // Same inactive-perspective gate as useTutorDashboard (student side).
    enabled: !!companyId && !!params,
  });
};
